// frontend/src/pages/ConfiguracionInformeEmail.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import weeklyReportService from '../services/weeklyReportService';
import Modal from '../components/Modal';
import './ConfiguracionInformeEmail.css';
import Loading from '../components/Loading';

// ── Constantes ──────────────────────────────────────────────────────────────

const DAY_NAMES = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

// Horas en punto disponibles para envío automático (00:00 … 23:00)
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}:00`);

// Normaliza cualquier hora guardada a la hora en punto más cercana (por si quedó algo con minutos de antes)
function toHourOnly(t) {
  if (!t) return '09:00';
  const h = parseInt(t.substring(0, 2), 10) || 0;
  return `${String(h).padStart(2, '0')}:00`;
}

const FIELD_OPTIONS = [
  // Contexto
  { key: 'material_type',    label: 'Tipo de material',    group: 'Contexto' },
  { key: 'status',           label: 'Estado',              group: 'Contexto' },
  { key: 'client',           label: 'Cliente',             group: 'Contexto' },
  { key: 'proyecto',         label: 'Proyecto',            group: 'Contexto' },
  { key: 'desarrolla',       label: 'Desarrolla',          group: 'Contexto' },
  // Tiempo
  { key: 'publication_time', label: 'Hora de publicación', group: 'Tiempo' },
  { key: 'validation_date',  label: 'Fecha de validación', group: 'Tiempo' },
  // Personas
  { key: 'editor',           label: 'Editor',              group: 'Personas' },
  { key: 'creator',          label: 'Creador',             group: 'Personas' },
  { key: 'influencer',       label: 'Influencer',          group: 'Personas' },
  // Contenido
  { key: 'copy',             label: 'Copy (snippet)',      group: 'Contenido' },
  { key: 'hashtags',         label: 'Hashtags',            group: 'Contenido' },
  { key: 'mentions',         label: 'Menciones',           group: 'Contenido' },
  { key: 'creative_idea',    label: 'Idea creativa',       group: 'Contenido' },
  { key: 'brief',            label: 'Brief',               group: 'Contenido' },
  // Patrocinio
  { key: 'post_patrocinado', label: 'Post patrocinado',    group: 'Patrocinio' },
  { key: 'patrocinio_monto', label: 'Monto patrocinio',    group: 'Patrocinio' },
];

const FIELD_GROUPS = ['Contexto', 'Tiempo', 'Personas', 'Contenido', 'Patrocinio'];
const DEFAULT_VISIBLE = ['material_type', 'publication_time', 'status', 'editor'];

const MATERIAL_TYPE_OPTIONS = [
  { value: 'image',    label: 'Imagen' },
  { value: 'video',    label: 'Video' },
  { value: 'carousel', label: 'Carrusel' },
  { value: 'story',    label: 'Story' },
  { value: 'reel',     label: 'Reel' },
];

const STATUS_OPTIONS = [
  { value: 'draft',                label: 'Borrador',            color: '#94a3b8' },
  { value: 'brief_pending',        label: 'Brief Pendiente',     color: '#f59e0b' },
  { value: 'creative_development', label: 'Desarrollo',          color: '#3b82f6' },
  { value: 'creative_review',      label: 'Revisión Idea',       color: '#8b5cf6' },
  { value: 'copy_review',          label: 'Revisión Copy',       color: '#a855f7' },
  { value: 'content_review',       label: 'Revisión Contenido',  color: '#14b8a6' },
  { value: 'final_review',         label: 'Revisión Final',      color: '#f59e0b' },
  { value: 'approved',             label: 'Aprobado',            color: '#22c55e' },
  { value: 'scheduled',            label: 'Programado',          color: '#10b981' },
  { value: 'published',            label: 'Publicado',           color: '#16a34a' },
  { value: 'rejected',             label: 'Rechazado',           color: '#ef4444' },
  { value: 'no_publicado',         label: 'No Publicado',        color: '#6b7280' },
];

const PLATFORM_COLORS_MAP = {
  instagram: '#E1306C',
  facebook:  '#1877F2',
  tiktok:    '#010101',
  twitter:   '#000000',
  linkedin:  '#0A66C2',
  pinterest: '#E60023',
};

const EMPTY_FILTERS = { platform: [], desarrolla: [], material_type: [], status: [] };

const EMPTY_FORM = {
  nombre: '',
  weeks_count: 1,
  send_days: [],
  send_time: '09:00',
  recipients: [],
  visible_fields: DEFAULT_VISIBLE,
  filters: EMPTY_FILTERS,
  is_active: true,
};

// Pasos del asistente. El último (Revisión) concentra vista previa, guardado y envío manual.
const STEPS = [
  { key: 'general',  label: 'General',  icon: 'bi-pencil-fill', hint: 'Nombre, estado y período' },
  { key: 'filtros',  label: 'Posts',    icon: 'bi-funnel-fill', hint: 'Qué posts se incluyen' },
  { key: 'campos',   label: 'Campos',   icon: 'bi-sliders',     hint: 'Qué se ve en cada card' },
  { key: 'envio',    label: 'Envío',    icon: 'bi-send-fill',   hint: 'Destinatarios y frecuencia' },
  { key: 'revision', label: 'Revisión', icon: 'bi-eye-fill',    hint: 'Vista previa y guardado' },
];
const LAST_STEP = STEPS.length - 1;

// ── Helpers ──────────────────────────────────────────────────────────────────

function getInitials(name) {
  const parts = (name || '').trim().split(' ').filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return 'US';
}

function dayLabels(days, time) {
  if (!days || days.length === 0) return 'Sin envío automático';
  const dStr = [...days].sort((a, b) => a - b).map((d) => DAY_NAMES[d]).join(', ');
  return time ? `${dStr} · ${formatTime(time)}` : dStr;
}

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'pm' : 'am';
  const h12  = hour % 12 || 12;
  return `${h12}:${m} ${ampm}`;
}

function weeksLabel(n) {
  return n === 1 ? 'Esta semana' : `${n} semanas`;
}

// ── Component ────────────────────────────────────────────────────────────────

const ConfiguracionInformeEmail = () => {
  const [configs, setConfigs]         = useState([]);
  const [selectedId, setSelectedId]   = useState(null);   // ID del config abierto
  const [form, setForm]               = useState(EMPTY_FORM);
  const [isNew, setIsNew]             = useState(false);   // true = creando
  const [step, setStep]               = useState(0);       // paso activo del asistente

  const [users, setUsers]             = useState([]);
  const [platforms, setPlatforms]     = useState([]);
  const [desarrollaOpts, setDesarrollaOpts] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [sending, setSending]         = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // ID a eliminar

  // Vista previa
  const [previewHtml, setPreviewHtml]       = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewDebRef  = useRef(null);
  const refreshPreviewRef = useRef(null); // siempre apunta al refreshPreview del último render (form actual)

  // ── Carga inicial ──
  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [rawConfigs, usersData, plats, desArr] = await Promise.all([
        weeklyReportService.getConfig(),
        weeklyReportService.getUsers(),
        weeklyReportService.getPlatforms(),
        weeklyReportService.getDesarrolla(),
      ]);
      const list = Array.isArray(rawConfigs) ? rawConfigs : [];
      setConfigs(list);
      setUsers(Array.isArray(usersData) ? usersData : []);
      setPlatforms(Array.isArray(plats) ? plats : []);
      setDesarrollaOpts(Array.isArray(desArr) ? desArr : []);

      // Seleccionar el primero automáticamente si existe
      if (list.length > 0) openConfig(list[0]);
    } catch (err) {
      toast.error('Error al cargar configuraciones');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ── Abrir un config existente ──
  const openConfig = (config) => {
    setIsNew(false);
    setSelectedId(config.id);
    setStep(LAST_STEP); // un informe existente se abre en la Revisión (resumen + vista previa)
    const savedFilters = config.filters || {};
    setForm({
      nombre:         config.nombre || '',
      weeks_count:    config.weeks_count || 1,
      send_days:      config.send_days || [],
      send_time:      toHourOnly(config.send_time),
      recipients:     (config.recipients || []).map(Number),
      visible_fields: config.visible_fields?.length ? config.visible_fields : DEFAULT_VISIBLE,
      filters: {
        platform:      savedFilters.platform      || [],
        desarrolla:    (savedFilters.desarrolla   || []).map(Number),
        material_type: savedFilters.material_type || [],
        status:        savedFilters.status        || [],
      },
      is_active:      config.is_active ?? true,
    });
    setPreviewHtml('');
  };

  // ── Nuevo informe ──
  const handleNew = () => {
    setIsNew(true);
    setSelectedId(null);
    setStep(0);
    setForm({ ...EMPTY_FORM, nombre: `Informe ${configs.length + 1}` });
    setPreviewHtml('');
  };

  // ── Guardar ──
  const handleSave = async () => {
    if (!form.nombre.trim()) { toast.info('Ponle un nombre al informe'); return; }
    setSaving(true);
    try {
      const payload = {
        nombre:         form.nombre,
        weeks_count:    form.weeks_count,
        send_days:      form.send_days,
        send_time:      form.send_time || '09:00',
        recipients:     form.recipients,
        visible_fields: form.visible_fields,
        filters:        form.filters,
        is_active:      form.is_active,
      };

      let saved;
      if (isNew) {
        saved = await weeklyReportService.createConfig(payload);
        setConfigs((prev) => [...prev, saved]);
        setIsNew(false);
        setSelectedId(saved.id);
        toast.success('Informe creado');
      } else {
        saved = await weeklyReportService.updateConfig(selectedId, payload);
        setConfigs((prev) => prev.map((c) => (c.id === saved.id ? saved : c)));
        toast.success('Cambios guardados');
      }
    } catch (err) {
      toast.error('Error al guardar');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // ── Guardar silencioso para preview ──
  const silentSave = async () => {
    if (!selectedId) return;
    const payload = {
      nombre:         form.nombre,
      weeks_count:    form.weeks_count,
      send_days:      form.send_days,
      send_time:      form.send_time || '09:00',
      recipients:     form.recipients,
      visible_fields: form.visible_fields,
      filters:        form.filters,
      is_active:      form.is_active,
    };
    await weeklyReportService.updateConfig(selectedId, payload);
  };

  // ── Eliminar ──
  const handleDelete = async (id) => {
    try {
      await weeklyReportService.deleteConfig(id);
      const remaining = configs.filter((c) => c.id !== id);
      setConfigs(remaining);
      setConfirmDelete(null);
      if (selectedId === id) {
        if (remaining.length > 0) openConfig(remaining[0]);
        else { setSelectedId(null); setIsNew(false); setForm(EMPTY_FORM); setPreviewHtml(''); setStep(0); }
      }
      toast.success('Informe eliminado');
    } catch (err) {
      toast.error('Error al eliminar');
      console.error(err);
    }
  };

  // ── Envío manual ──
  const handleSendNow = async () => {
    if (!selectedId) { toast.info('Primero guarda el informe'); return; }
    if (form.recipients.length === 0) { toast.info('Agrega al menos un destinatario'); return; }
    setSending(true);
    try {
      await silentSave();
      const res = await weeklyReportService.sendNow(selectedId);
      toast.success(res.detail || 'Informe enviado');
    } catch (err) {
      toast.error('Error al enviar');
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  // ── Vista previa con debounce ──
  const schedulePreview = useCallback(() => {
    if (!selectedId) return;
    clearTimeout(previewDebRef.current);
    previewDebRef.current = setTimeout(refreshPreview, 800);
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedId && !isNew) schedulePreview();
  }, [form.weeks_count, form.visible_fields, selectedId, schedulePreview]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshPreview = async () => {
    if (!selectedId) return;
    setPreviewLoading(true);
    try {
      await silentSave();
      const html = await weeklyReportService.getPreviewHtml(selectedId);
      setPreviewHtml(html);
    } catch (e) {
      console.error('Error cargando preview:', e);
    } finally {
      setPreviewLoading(false);
    }
  };
  refreshPreviewRef.current = refreshPreview;

  // Al llegar a la Revisión se genera la vista previa con el formulario ACTUAL (mismo efecto que
  // el botón "Actualizar"). Cancela cualquier refresco diferido pendiente para que no lo pise.
  useEffect(() => {
    if (step !== LAST_STEP || !selectedId || isNew) return;
    clearTimeout(previewDebRef.current);
    if (refreshPreviewRef.current) refreshPreviewRef.current();
  }, [step, selectedId, isNew]);

  // ── Toggle helpers ──
  const toggleDay      = (d)   => setForm((f) => ({ ...f, send_days:      f.send_days.includes(d)      ? f.send_days.filter((x) => x !== d)      : [...f.send_days, d] }));
  const toggleRecip    = (id)  => setForm((f) => ({ ...f, recipients:     f.recipients.includes(id)    ? f.recipients.filter((x) => x !== id)    : [...f.recipients, id] }));
  const toggleField    = (key) => setForm((f) => ({ ...f, visible_fields: f.visible_fields.includes(key) ? f.visible_fields.filter((x) => x !== key) : [...f.visible_fields, key] }));

  // Filtros: togglea un valor dentro de una clave del objeto filters
  const toggleFilter = (filterKey, value) =>
    setForm((f) => {
      const current = f.filters[filterKey] || [];
      const updated = current.includes(value)
        ? current.filter((x) => x !== value)
        : [...current, value];
      return { ...f, filters: { ...f.filters, [filterKey]: updated } };
    });

  const hasChanges = selectedId || isNew;

  // ── Navegación del asistente ──
  const goNext = () => {
    if (step === 0 && !form.nombre.trim()) { toast.info('Ponle un nombre al informe'); return; }
    setStep((s) => Math.min(s + 1, LAST_STEP));
  };
  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  // ── Textos de resumen (paso Revisión) ──
  const userName = (u) => u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username;
  const filterSummary = [
    {
      label: 'Plataforma',
      values: (form.filters.platform || []).map((n) => {
        const p = platforms.find((x) => x.name === n);
        return p?.get_name_display || n;
      }),
    },
    {
      label: 'Desarrolla',
      values: (form.filters.desarrolla || []).map((id) => {
        const d = desarrollaOpts.find((x) => x.id === id);
        return d ? (d.name_display || d.name) : `#${id}`;
      }),
    },
    {
      label: 'Tipo de contenido',
      values: (form.filters.material_type || []).map((v) => MATERIAL_TYPE_OPTIONS.find((o) => o.value === v)?.label || v),
    },
    {
      label: 'Estado',
      values: (form.filters.status || []).map((v) => STATUS_OPTIONS.find((o) => o.value === v)?.label || v),
    },
  ];
  const selectedRecipients = users.filter((u) => form.recipients.includes(u.id));

  // ── Render ──
  if (loading) {
    return (
      <div className="g-page informe-page">
        <Loading fullScreen={false} mensaje="Cargando informes" />
      </div>
    );
  }

  // ── Pasos ──
  const renderGeneral = () => (
    <div className="iw-step-body">
      <section className="iw-section">
        <label className="g-label" htmlFor="iw-nombre">Nombre del informe</label>
        <input
          id="iw-nombre"
          className="iw-input"
          type="text"
          placeholder="Ej: Informe equipo creativo"
          value={form.nombre}
          onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
        />
      </section>

      <section className="iw-section">
        <span className="g-label">Estado</span>
        <button
          type="button"
          role="switch"
          aria-checked={form.is_active}
          className={`iw-switch-row${form.is_active ? ' on' : ''}`}
          onClick={() => setForm((f) => ({ ...f, is_active: !f.is_active }))}
        >
          <span className="iw-switch" aria-hidden="true" />
          <span className="iw-switch-text">
            <strong>{form.is_active ? 'Activo' : 'Pausado'}</strong>
            <small>{form.is_active ? 'Se enviará en los días configurados.' : 'No se enviará automáticamente.'}</small>
          </span>
        </button>
      </section>

      <section className="iw-section">
        <span className="g-label">Semanas a mostrar</span>
        <div className="iw-segmented" role="radiogroup" aria-label="Semanas a mostrar">
          {[1, 2, 3, 4].map((n) => (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={form.weeks_count === n}
              className={`iw-seg${form.weeks_count === n ? ' active' : ''}`}
              onClick={() => setForm((f) => ({ ...f, weeks_count: n }))}
            >
              {n === 1 ? 'Esta semana' : `${n} sem.`}
            </button>
          ))}
        </div>
        <p className="g-helper-text">Cubre desde el lunes de la semana en curso. Período: {weeksLabel(form.weeks_count).toLowerCase()}.</p>
      </section>
    </div>
  );

  const renderFiltros = () => (
    <div className="iw-step-body">
      <p className="iw-lead">Sin selección = se incluyen todos. Selecciona para restringir.</p>

      {/* Plataforma */}
      <section className="iw-section">
        <span className="g-label">Plataforma</span>
        <div className="iw-pills">
          {platforms.map((p) => {
            const active = (form.filters.platform || []).includes(p.name);
            const color = PLATFORM_COLORS_MAP[p.name] || '#64748b';
            return (
              <button
                key={p.id}
                type="button"
                aria-pressed={active}
                className={`iw-pill${active ? ' active' : ''}`}
                style={active ? { background: color + '20', borderColor: color, color } : {}}
                onClick={() => toggleFilter('platform', p.name)}
              >
                {p.get_name_display || p.name}
              </button>
            );
          })}
          {platforms.length === 0 && <span className="iw-empty-inline">Sin plataformas configuradas</span>}
        </div>
      </section>

      {/* Desarrolla */}
      <section className="iw-section">
        <span className="g-label">Desarrolla</span>
        <div className="iw-pills">
          {desarrollaOpts.map((d) => {
            const active = (form.filters.desarrolla || []).includes(d.id);
            const color = d.color || '#64748b';
            return (
              <button
                key={d.id}
                type="button"
                aria-pressed={active}
                className={`iw-pill${active ? ' active' : ''}`}
                style={active ? { background: color + '20', borderColor: color, color } : {}}
                onClick={() => toggleFilter('desarrolla', d.id)}
              >
                {d.name_display || d.name}
              </button>
            );
          })}
          {desarrollaOpts.length === 0 && <span className="iw-empty-inline">Sin opciones</span>}
        </div>
      </section>

      {/* Tipo de contenido */}
      <section className="iw-section">
        <span className="g-label">Tipo de contenido</span>
        <div className="iw-pills">
          {MATERIAL_TYPE_OPTIONS.map(({ value, label }) => {
            const active = (form.filters.material_type || []).includes(value);
            return (
              <button
                key={value}
                type="button"
                aria-pressed={active}
                className={`iw-pill${active ? ' active' : ''}`}
                onClick={() => toggleFilter('material_type', value)}
              >
                {label}
              </button>
            );
          })}
        </div>
      </section>

      {/* Estado */}
      <section className="iw-section">
        <span className="g-label">Estado</span>
        <div className="iw-pills">
          {STATUS_OPTIONS.map(({ value, label, color }) => {
            const active = (form.filters.status || []).includes(value);
            return (
              <button
                key={value}
                type="button"
                aria-pressed={active}
                className={`iw-pill${active ? ' active' : ''}`}
                style={active ? { background: color + '20', borderColor: color, color } : {}}
                onClick={() => toggleFilter('status', value)}
              >
                {label}
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );

  const renderCampos = () => (
    <div className="iw-step-body">
      <p className="iw-lead">
        Elige qué datos aparecen en cada card del informe.{' '}
        <strong>{form.visible_fields.length}</strong> de {FIELD_OPTIONS.length} seleccionados.
      </p>
      {FIELD_GROUPS.map((group) => {
        const groupFields = FIELD_OPTIONS.filter((f) => f.group === group);
        return (
          <section key={group} className="iw-section">
            <span className="g-label">{group}</span>
            <div className="iw-fields-grid">
              {groupFields.map(({ key, label }) => {
                const on = form.visible_fields.includes(key);
                return (
                  <button
                    key={key}
                    type="button"
                    role="switch"
                    aria-checked={on}
                    className={`iw-field${on ? ' on' : ''}`}
                    onClick={() => toggleField(key)}
                  >
                    <span className="iw-switch iw-switch-sm" aria-hidden="true" />
                    <span className="iw-field-label">{label}</span>
                  </button>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );

  const renderEnvio = () => (
    <div className="iw-step-body">
      {/* Destinatarios */}
      <section className="iw-section">
        <div className="iw-section-head">
          <span className="g-label">Destinatarios</span>
          {form.recipients.length > 0 && (
            <span className="iw-count">
              {form.recipients.length} seleccionado{form.recipients.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="iw-recipients">
          {users.length === 0 && <p className="iw-empty-inline">No hay usuarios disponibles.</p>}
          {users.map((u) => {
            const fullName = userName(u);
            const isSelected = form.recipients.includes(u.id);
            return (
              <button
                key={u.id}
                type="button"
                aria-pressed={isSelected}
                className={`iw-recipient${isSelected ? ' selected' : ''}`}
                onClick={() => toggleRecip(u.id)}
              >
                <span className="iw-avatar">{getInitials(fullName)}</span>
                <span className="iw-recipient-info">
                  <span className="iw-recipient-name">{fullName}</span>
                  <span className="iw-recipient-email">{u.email || 'Sin email'}</span>
                </span>
                <span className="iw-check" aria-hidden="true">{isSelected && <i className="bi bi-check-lg" />}</span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Días y hora */}
      <section className="iw-section">
        <span className="g-label">Días de envío automático</span>
        <div className="iw-days">
          {DAY_NAMES.map((name, i) => (
            <button
              key={i}
              type="button"
              aria-pressed={form.send_days.includes(i)}
              className={`iw-day${form.send_days.includes(i) ? ' active' : ''}`}
              onClick={() => toggleDay(i)}
            >
              {name}
            </button>
          ))}
        </div>

        {form.send_days.length > 0 ? (
          <div className="iw-time-box">
            <label className="g-label" htmlFor="iw-hora">Hora de envío</label>
            <select
              id="iw-hora"
              className="iw-input iw-select"
              value={form.send_time || '09:00'}
              onChange={(e) => setForm((f) => ({ ...f, send_time: e.target.value }))}
            >
              {HOUR_OPTIONS.map((h) => (
                <option key={h} value={h}>{formatTime(h)}</option>
              ))}
            </select>
            <p className="g-helper-text">
              Se enviará los {dayLabels(form.send_days)} a las {formatTime(form.send_time || '09:00')}
            </p>
          </div>
        ) : (
          <p className="g-helper-text">Sin días → solo envío manual.</p>
        )}
      </section>
    </div>
  );

  const SummaryRow = ({ icon, title, goTo, children }) => (
    <div className="iw-sum-row">
      <div className="iw-sum-icon"><i className={`bi ${icon}`} /></div>
      <div className="iw-sum-main">
        <div className="iw-sum-title">{title}</div>
        <div className="iw-sum-value">{children}</div>
      </div>
      <button type="button" className="iw-link" onClick={() => setStep(goTo)}>Editar</button>
    </div>
  );

  const renderRevision = () => (
    <div className="iw-review">
      {/* Resumen */}
      <div className="iw-summary">
        <SummaryRow icon="bi-pencil-fill" title="Informe" goTo={0}>
          <strong>{form.nombre || 'Sin nombre'}</strong>{' '}
          <span className={`g-badge ${form.is_active ? 'g-badge-success' : 'g-badge-neutral'}`}>
            {form.is_active ? 'Activo' : 'Pausado'}
          </span>
          <div className="iw-sum-sub">{weeksLabel(form.weeks_count)}</div>
        </SummaryRow>

        <SummaryRow icon="bi-funnel-fill" title="Posts incluidos" goTo={1}>
          {filterSummary.every((g) => g.values.length === 0) ? (
            <span className="iw-muted">Todos los posts</span>
          ) : (
            filterSummary.filter((g) => g.values.length > 0).map((g) => (
              <div key={g.label} className="iw-sum-sub"><strong>{g.label}:</strong> {g.values.join(', ')}</div>
            ))
          )}
        </SummaryRow>

        <SummaryRow icon="bi-sliders" title="Campos en la card" goTo={2}>
          {form.visible_fields.length === 0 ? (
            <span className="iw-muted">Ninguno</span>
          ) : (
            <div className="iw-chips">
              {FIELD_OPTIONS.filter((f) => form.visible_fields.includes(f.key)).map((f) => (
                <span key={f.key} className="iw-chip">{f.label}</span>
              ))}
            </div>
          )}
        </SummaryRow>

        <SummaryRow icon="bi-send-fill" title="Envío" goTo={3}>
          <div>{dayLabels(form.send_days, form.send_days.length > 0 ? form.send_time : null)}</div>
          <div className="iw-sum-sub">
            {selectedRecipients.length === 0
              ? <span className="iw-warn">Sin destinatarios</span>
              : `${selectedRecipients.length} destinatario${selectedRecipients.length !== 1 ? 's' : ''}: ${selectedRecipients.map(userName).join(', ')}`}
          </div>
        </SummaryRow>
      </div>

      {/* Vista previa */}
      <div className="iw-preview">
        <div className="iw-preview-head">
          <span className="g-label">Vista previa</span>
          {selectedId && !isNew && (
            <button type="button" className="g-btn g-btn-secondary g-btn-sm" onClick={refreshPreview} disabled={previewLoading}>
              {previewLoading ? '…' : (<><i className="bi bi-arrow-clockwise"></i> Actualizar</>)}
            </button>
          )}
        </div>

        {isNew ? (
          <div className="iw-preview-empty">
            <i className="bi bi-stars" />
            <p>Guarda el informe para ver la vista previa y habilitar el envío.</p>
          </div>
        ) : !selectedId ? (
          <div className="iw-preview-empty">
            <i className="bi bi-envelope" />
            <p>Selecciona un informe para ver la vista previa</p>
          </div>
        ) : previewLoading ? (
          <Loading fullScreen={false} mensaje="Generando vista previa" />
        ) : previewHtml ? (
          <div className="iw-preview-frame">
            <iframe title="Vista previa" sandbox="allow-same-origin" srcDoc={previewHtml} />
          </div>
        ) : (
          <div className="iw-preview-empty">
            <i className="bi bi-eye" />
            <p>Haz clic en "Actualizar" para generar la vista previa</p>
          </div>
        )}
      </div>
    </div>
  );

  const stepBodies = [renderGeneral, renderFiltros, renderCampos, renderEnvio, renderRevision];

  const saveLabel = saving
    ? 'Guardando…'
    : isNew
      ? (<><i className="bi bi-stars"></i> Crear informe</>)
      : (<><i className="bi bi-save-fill"></i> Guardar cambios</>);

  return (
    <div className="g-page informe-page">
      {/* Header */}
      <div className="g-page-header">
        <div>
          <h1 className="g-page-title">
            <i className="bi bi-envelope-paper-fill" style={{ marginRight: 10, color: 'var(--g-lima)' }}></i>
            Informes Semanales por Email
          </h1>
          <p className="g-page-subtitle">Crea múltiples informes con distintos destinatarios, campos y frecuencias de envío.</p>
        </div>
      </div>

      <div className="iw-layout">

        {/* ── Lista de informes ── */}
        <aside className="iw-list-panel">
          <div className="iw-list-header">
            <span className="iw-list-title">Mis informes</span>
            <button type="button" className="g-btn g-btn-primary g-btn-sm" onClick={handleNew}>+ Nuevo</button>
          </div>

          <div className="iw-list">
            {configs.length === 0 && !isNew && (
              <div className="iw-list-empty">
                <i className="bi bi-envelope" style={{ fontSize: 26, opacity: 0.4 }}></i>
                <p>Sin informes aún</p>
                <button type="button" className="g-btn g-btn-primary g-btn-sm" onClick={handleNew}>Crear el primero</button>
              </div>
            )}

            {/* Item "nuevo" en creación */}
            {isNew && (
              <div className="iw-item active">
                <div className="iw-item-icon new"><i className="bi bi-stars"></i></div>
                <div className="iw-item-info">
                  <span className="iw-item-name">{form.nombre || 'Nuevo informe'}</span>
                  <span className="iw-item-meta">Sin guardar</span>
                </div>
              </div>
            )}

            {configs.map((c) => (
              <div
                key={c.id}
                role="button"
                tabIndex={0}
                className={`iw-item${selectedId === c.id && !isNew ? ' active' : ''}`}
                onClick={() => openConfig(c)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openConfig(c); } }}
              >
                <div className={`iw-item-icon${c.is_active ? '' : ' inactive'}`}>
                  <i className={`bi ${c.is_active ? 'bi-envelope-fill' : 'bi-pause-circle-fill'}`}></i>
                </div>
                <div className="iw-item-info">
                  <span className="iw-item-name">{c.nombre}</span>
                  <span className="iw-item-meta">
                    {c.recipients?.length || 0} dest. · {dayLabels(c.send_days, c.send_time)}
                  </span>
                </div>
                <button
                  type="button"
                  className="iw-item-delete"
                  onClick={(e) => { e.stopPropagation(); setConfirmDelete(c.id); }}
                  onKeyDown={(e) => e.stopPropagation()}
                  title="Eliminar"
                  aria-label={`Eliminar ${c.nombre}`}
                >
                  <i className="bi bi-x"></i>
                </button>
              </div>
            ))}
          </div>
        </aside>

        {/* ── Asistente ── */}
        {hasChanges ? (
          <section className="iw-wizard">
            <header className="iw-wizard-head">
              <div className="iw-wizard-title">
                <h2>{form.nombre || 'Nuevo informe'}</h2>
                <span className={`g-badge ${isNew ? 'g-badge-warning' : form.is_active ? 'g-badge-success' : 'g-badge-neutral'}`}>
                  {isNew ? 'Sin guardar' : form.is_active ? 'Activo' : 'Pausado'}
                </span>
              </div>

              <nav className="iw-stepper" aria-label="Pasos del informe">
                {STEPS.map((s, i) => (
                  <button
                    key={s.key}
                    type="button"
                    className={`iw-step${i === step ? ' current' : ''}${i < step ? ' done' : ''}`}
                    aria-current={i === step ? 'step' : undefined}
                    onClick={() => setStep(i)}
                  >
                    <span className="iw-step-dot">{i < step ? <i className="bi bi-check-lg" /> : i + 1}</span>
                    <span className="iw-step-text">
                      <span className="iw-step-label">{s.label}</span>
                    </span>
                  </button>
                ))}
              </nav>
            </header>

            <div className="iw-wizard-intro">
              <i className={`bi ${STEPS[step].icon}`} />
              <div>
                <strong>Paso {step + 1} de {STEPS.length} · {STEPS[step].label}</strong>
                <span>{STEPS[step].hint}</span>
              </div>
            </div>

            <div className="iw-wizard-body">{stepBodies[step]()}</div>

            <footer className="iw-wizard-foot">
              <div className="iw-foot-left">
                {step > 0 && (
                  <button type="button" className="g-btn g-btn-ghost" onClick={goBack}>
                    <i className="bi bi-arrow-left"></i> Atrás
                  </button>
                )}
              </div>
              <div className="iw-foot-right">
                {step < LAST_STEP ? (
                  <>
                    <button type="button" className="g-btn g-btn-secondary" onClick={handleSave} disabled={saving}>
                      {saveLabel}
                    </button>
                    <button type="button" className="g-btn g-btn-primary" onClick={goNext}>
                      Siguiente <i className="bi bi-arrow-right"></i>
                    </button>
                  </>
                ) : (
                  <>
                    {!isNew && (
                      <button type="button" className="g-btn g-btn-secondary" onClick={handleSendNow} disabled={sending}>
                        {sending ? 'Enviando…' : (<><i className="bi bi-send-fill"></i> Enviar ahora</>)}
                      </button>
                    )}
                    <button type="button" className="g-btn g-btn-primary" onClick={handleSave} disabled={saving}>
                      {saveLabel}
                    </button>
                  </>
                )}
              </div>
            </footer>
          </section>
        ) : (
          <section className="iw-wizard">
            <div className="iw-no-selection">
              <i className="bi bi-clipboard2-data" style={{ fontSize: 38, opacity: 0.4 }}></i>
              <p>Selecciona un informe para editarlo<br />o crea uno nuevo.</p>
              <button type="button" className="g-btn g-btn-primary" onClick={handleNew}>
                <i className="bi bi-plus-lg"></i> Nuevo informe
              </button>
            </div>
          </section>
        )}
      </div>

      {/* ── Modal confirmación de borrado ── */}
      <Modal
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        minimal
        size="sm"
        title="¿Eliminar este informe?"
        footer={
          <>
            <button type="button" className="g-btn g-btn-ghost" onClick={() => setConfirmDelete(null)}>
              Cancelar
            </button>
            <button type="button" className="g-btn iw-delete-confirm" onClick={() => handleDelete(confirmDelete)}>
              Eliminar
            </button>
          </>
        }
      >
        <p className="iw-delete-text">Esta acción no se puede deshacer.</p>
      </Modal>
    </div>
  );
};

export default ConfiguracionInformeEmail;
